import { getExportOriginalSearchUrl } from '@aibees/elephant/feature/exports-management-table/utils';
import { IconButton } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Box, Tooltip } from '@mui/material';
import { memo, useCallback } from 'react';
import type { ExportTableViewRecordsActionProps as Props } from './types';

const SINGLE_RECORD_TOOLTIP = (
  <>
    {"View search doesn't apply"}
    <br />
    to single record exports
  </>
);

/**
 * "View Search" eye button: reopens the ORIGINAL search that produced this export
 * via the short `?export-search=<id>` link (see `useApplyExportSearch`). The
 * Include / Exclude / Include-AND cascade that used to live on this button now
 * lives in the ⋯ dropdown ("Add to Search").
 *
 * Disabled for single-record exports (records === 1): there is no multi-record
 * search to return to.
 */
export const ExportTableViewRecordsAction = memo(
  ({ className, item }: Props) => {
    const isSingleRecord = item.records === 1;

    const handleClick = useCallback(() => {
      const url = getExportOriginalSearchUrl(item);
      if (url) window.open(url, '_blank');
    }, [item]);

    return (
      <Box
        className={classnames('ExportTableViewRecordsAction', className)}
        display="flex"
        alignItems="center"
      >
        {isSingleRecord ? (
          // A disabled MUI button eats pointer events, so the shared IconButton's
          // own tooltip can't fire; anchor the explanatory tooltip to a span
          // wrapper (MUI's documented pattern) and mute the inner tooltip.
          <Tooltip title={SINGLE_RECORD_TOOLTIP} placement="left" arrow>
            <Box component="span" display="inline-flex">
              <IconButton
                icon="eye-outlined"
                variant="outlined"
                size="xsmall"
                tooltipTitle=""
                disabled
              />
            </Box>
          </Tooltip>
        ) : (
          <IconButton
            icon="eye-outlined"
            variant="outlined"
            size="xsmall"
            tooltipTitle="View Search"
            onClick={handleClick}
          />
        )}
      </Box>
    );
  }
);

ExportTableViewRecordsAction.displayName = 'ExportTableViewRecordsAction';
